import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import PriceProduct from '../price-product'
import CTA from '../cta'
import priceButtonGroupRecipe, {PRICE_VARIANT_MAP, CTA_SIZE_MAP} from './index.recipe'
import {hasValidPrice, hasValidString} from '../../../utils/utils'

/**
 * PriceButtonGroup component displays a price label alongside a CTA button.
 * Used when showing product pricing with a single action button.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.price - Price configuration object.
 * @param {number} props.price.value - The numeric price value.
 * @param {number} props.price.valueListPrice - The numeric list price value.
 * @param {string} [props.price.currency] - Currency code (e.g., 'USD', 'EUR').
 * @param {string} [props.price.label] - Optional label to display before price.
 * @param {Object} [props.cta] - CTA button configuration.
 * @param {string} props.cta.label - Button label text.
 * @param {Object} props.cta.action - Button action configuration.
 * @param {'large'|'small'} props.size- Button size variant
 * @param {Object} trackingContext - Context for tracking purposes.
 * @param {string} trackingContext.moduleName - Module name for tracking.
 * @param {string} variant - Background and price color variant.
 * @returns {JSX.Element} Rendered price and button group.
 */
const PriceButtonGroup = ({
    price,
    cta,
    size = priceButtonGroupRecipe.defaultVariants.size,
    trackingContext,
    layout,
    variant
}) => {
    const hasPrice = hasValidPrice(price?.value)
    const hasCtaLabel = hasValidString(cta?.label)

    const recipe = useSlotRecipe({recipe: priceButtonGroupRecipe})
    const styles = useMemo(
        () => recipe({size, hasPrice, layout, variant}),
        [recipe, size, hasPrice, layout, variant]
    )

    if (!hasPrice && !hasCtaLabel) return null

    return (
        <Box css={styles.root}>
            {hasPrice && (
                <PriceProduct {...price} layout={layout} variant={PRICE_VARIANT_MAP[variant]} />
            )}
            {hasCtaLabel && (
                <CTA
                    {...cta}
                    size={CTA_SIZE_MAP[`${size}|${hasPrice}`]}
                    css={styles.cta}
                    trackingContext={trackingContext}
                />
            )}
        </Box>
    )
}

PriceButtonGroup.propTypes = {
    price: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        valueListPrice: PropTypes.number,
        label: PropTypes.string,
        hidePromotionSignals: PropTypes.bool
    }),
    cta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        })
    }),
    size: PropTypes.oneOf(Object.keys(priceButtonGroupRecipe.variants.size)),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    }),
    layout: PropTypes.oneOf(Object.keys(priceButtonGroupRecipe.variants.layout)),
    variant: PropTypes.oneOf(Object.keys(priceButtonGroupRecipe.variants.variant))
}

export default PriceButtonGroup
